import { Avatar, Box, Typography } from "@mui/material";
import {
  AccessTime,
  ChatBubbleOutline,
  ChevronRight,
} from "@mui/icons-material";
import { tokens } from "../../../theme";

type MemoryBookStatus = "overdue" | "in_danger" | "safe";

export interface MemoryBookEntry {
  id: number;
  deceasedName: string;
  deceasedAvatarUrl?: string;
  currentAssigneeId?: number;
  currentAssigneeName?: string; 
  currentAssigneeAvatar?: string;
  streamId?: number;
  timeInfo: string;
  commentCount: number;
  progressPercent: number;
  status: MemoryBookStatus;
}

const STATUS_CONFIG: Record<MemoryBookStatus, { progressColor: string }> = {
  overdue: { progressColor: "#E11D48" },
  in_danger: { progressColor: "#f59e0b" },
  safe: { progressColor: "#10b981" },
};

interface MemoryBookCardProps {
  entry: MemoryBookEntry;
  colors: ReturnType<typeof tokens>;
  onClick: () => void;
}

const MemoryBookCard = ({ entry, colors, onClick }: MemoryBookCardProps) => {
  const cfg = STATUS_CONFIG[entry.status];

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        borderRadius: "20px",
        backgroundColor: colors.primary[900],
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: colors.primary[700] + "40" },
      }}
    >
      {/* Avatar */}
      <Avatar
        src={entry.deceasedAvatarUrl}
        sx={{ width: 38, height: 38, flexShrink: 0 }}
      >
        {entry.deceasedName[0]}
      </Avatar>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.8rem",
            color: colors.primary[100],
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {entry.deceasedName}
        </Typography>

        {/* Progress bar */}
        <Box
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: colors.primary[300] + "30",
            my: 0.5,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${entry.progressPercent}%`,
              borderRadius: 2,
              backgroundColor: cfg.progressColor,
              transition: "width 0.4s ease",
            }}
          />
        </Box>

        {/* Meta row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <AccessTime
              sx={{ fontSize: 11, color: colors.primary[700], flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontSize: "0.65rem",
                color: colors.primary[700],
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {entry.timeInfo}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              flexShrink: 0,
            }}
          >
            <ChatBubbleOutline
              sx={{ fontSize: 11, color: colors.primary[700] }}
            />
            <Typography
              sx={{
                fontSize: "0.65rem",
                color: colors.primary[700],
                whiteSpace: "nowrap",
              }}
            >
              {entry.commentCount}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Chevron */}
      <ChevronRight
        sx={{ fontSize: 16, color: colors.primary[700], flexShrink: 0 }}
      />
    </Box>
  );
};

export default MemoryBookCard;
